import get from 'lodash/get';

import { getSelectValue, sortByKey } from '../../utils';
import { formatSelect, formatTextSelect } from '../Forms/utils';

import {
  AMENITY_MULTI_STATUS_OPTIONS,
  AMENITY_STATUS_OPTIONS,
} from '../Amenity/constants';
import { METHODS_FOR_DOORS_LOG } from '../Building/constants';
import {
  COMMUNITY_SCOPE_OPTIONS,
  COMMUNITY_STATUS_OPTIONS,
} from '../Community/constants';
import { DELIVERY_STATUS_TYPES } from '../Delivery/constants';
import { CATEGORIES_LIST, EVENTS_STATUS_TYPES } from '../Events/constants';
import { CALL_STATUS_OPTIONS } from '../RemoteDoorman/constants';
import { FEEDBACK_TYPES } from '../ServiceRequest/constants';

import {
  VIEW_OPTIONS,
  limitOptions,
  serviceRequestPendingResolved,
  visitorStatusFilterOptions,
} from '../../staticData';

export const pmcFilter = (listParams, pmcs, setOnChange) => {
  const key = 'pmc';
  const options = [
    ...formatSelect(
      Object.values(pmcs).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];
  const value = getSelectValue(listParams?.[key], options);
  let filter = {
    key,
    defaultValue: null,
    placeholder: 'Select PMC',
    isClearable: true,
    options,
    value,
  };
  if (setOnChange) {
    filter = { ...filter, setOnChange };
  }
  return filter;
};

export const buildingFilter = (
  listParams,
  buildings,
  setOnChange,
  multi = false,
  key = 'building',
) => {
  const options = [
    ...formatSelect(
      Object.values(buildings).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];
  const value = getSelectValue(listParams?.[key], options);
  let filter = {
    key,
    defaultValue: null,
    placeholder: 'Select building',
    isClearable: true,
    multi,
    options,
    value,
  };
  if (setOnChange) {
    filter = { ...filter, setOnChange };
  }
  return filter;
};

export const multiBuildingFilter = (listParams, buildings) => {
  const key = 'available_buildings';

  const options = [
    ...formatSelect(
      Object.values(buildings).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];

  const selectedStatuses = listParams?.[key]
    ?.toString()
    ?.split(',')
    ?.filter(Boolean);

  const value = selectedStatuses?.map((value) =>
    getSelectValue(value, options),
  );

  const filter = {
    key,
    defaultValue: null,
    placeholder: 'Select buildings',
    isClearable: true,
    multi: true,
    isMultiWithCheckbox: true,
    options,
    value,
  };

  return filter;
};

export const unitFilter = (listParams, units) => {
  const key = 'unit';
  const options = [
    ...formatSelect(Object.values(units).sort((a, b) => a.order - b.order)),
  ];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: null,
    placeholder: 'Select unit',
    isClearable: true,
    options,
    value,
  };
};

export const doorFilter = (listParams, doors) => {
  const key = 'door';
  const options = [...formatSelect(Object.values(doors))];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select Door',
    isClearable: true,
    options,
    value,
  };
};

export const actionFilter = (listParams, options) => {
  const key = 'action';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Linked Action',
    isClearable: true,
    options,
    value,
  };
};

export const approvalScopeFilter = (listParams) => {
  const key = 'approval_scope';
  const options = COMMUNITY_SCOPE_OPTIONS;
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'All Statuses',
    isClearable: true,
    options,
    value,
  };
};

export const sourceFilter = (listParams) => {
  const key = 'source';
  const options = [...METHODS_FOR_DOORS_LOG];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Opened Using',
    isClearable: true,
    options,
    value,
  };
};

export const limitFilter = (listParams) => {
  const key = 'limit';
  const options = [...formatSelect(Object.values(limitOptions))];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Items per page',
    isClearable: false,
    options,
    sortAlphabetically: false,
    value,
  };
};

export const viewFilter = (listParams, listParamsFormatter) => {
  const key = 'view';
  const options = VIEW_OPTIONS;
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: '',
    isClearable: false,
    options,
    value,
    listParamsFormatter,
  };
};

export const agentFilter = (listParams, options, listParamsFormatter) => {
  const key = 'cc_agent';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select call center agent',
    isClearable: true,
    options,
    value,
    listParamsFormatter,
  };
};

export const callStatusFilter = (listParams) => {
  const key = 'status';
  const options = CALL_STATUS_OPTIONS;
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select call status',
    isClearable: true,
    options,
    value,
  };
};

export const typeFilter = (listParams, options) => {
  const key = 'type';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select type',
    isClearable: true,
    options,
    value,
  };
};

export const accessCardsBatchFilter = (listParams, batchOptions) => {
  const key = 'access_card_batch';
  const options = [...formatSelect(batchOptions, 'name', 'id')];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Batch name',
    isClearable: true,
    options,
    value,
  };
};

export const actionsLogNamesFilter = (listParams, actionsLogNames) => {
  const key = 'action_name';
  const options = [...formatTextSelect(actionsLogNames)];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Filter by Action',
    isClearable: true,
    options,
    value,
  };
};

export const statusFilter = (listParams, serviceRequestsCounts) => {
  const options = serviceRequestsCounts
    ? [...formatSelect(Object.values(serviceRequestPendingResolved))].map(
        (option) => {
          if (!serviceRequestsCounts[option.value]) {
            return option;
          }
          const label = `${option.label} (${
            serviceRequestsCounts[option.value]
          })`;
          return {
            ...option,
            label,
          };
        },
      )
    : [...formatSelect(Object.values(serviceRequestPendingResolved))];
  const value = serviceRequestsCounts
    ? options.find((option) => get(listParams, 'status', null) === option.value)
    : get(listParams, 'status', null);
  return {
    key: 'status',
    defaultValue: get(listParams, 'status', null),
    placeholder: 'Select status',
    isClearable: true,
    options,
    value,
  };
};

export const dateRangeTypeFilter = (dateRangeTypeOptions, dateRangeType) => {
  const key = 'dateRangeType';
  const options = [...dateRangeTypeOptions];
  const value = getSelectValue(dateRangeType, options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Date Range Type',
    isClearable: false,
    options,
    value,
  };
};

export const visitorStatusFilter = (listParams) => {
  const key = 'status';
  const options = [...visitorStatusFilterOptions];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select visitor status',
    isClearable: true,
    options,
    value,
  };
};

export const roleFilter = (listParams, options) => {
  const key = 'role';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select role',
    isClearable: true,
    options,
    value,
  };
};

export const commonAreaFilter = (listParams) => {
  const key = 'common_area';
  const options = [{ label: 'Common Area', value: 'True' }]; // the API required to start with a capital letter in this case
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Is Common area',
    isClearable: true,
    options,
    value,
  };
};

export const amenityStatusFilter = (listParams) => {
  const key = 'status';
  const options = [...AMENITY_STATUS_OPTIONS];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select status',
    isClearable: true,
    options,
    value,
  };
};

export const amenityMultiStatusFilter = (listParams) => {
  const key = 'approval_statuses';
  const options = [...AMENITY_MULTI_STATUS_OPTIONS];
  const selectedStatuses = listParams?.[key]
    ?.toString()
    ?.split(',')
    ?.filter(Boolean);

  const value = selectedStatuses?.map((value) =>
    getSelectValue(value, options),
  );

  return {
    key,
    defaultValue: value,
    placeholder: 'Status',
    isClearable: true,
    options,
    value,
    multi: true,
    isMultiWithCheckbox: true,
  };
};

export const amenityFilter = (listParams, amenities) => {
  const key = 'amenity';
  const options = [...formatSelect(amenities)];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select amenity',
    isClearable: true,
    options,
    value,
  };
};

export const feedbackFilter = (listParams) => {
  const key = 'positive_feedback';
  const options = [...FEEDBACK_TYPES];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Select feedback type',
    isClearable: true,
    options,
    value,
  };
};

export const openDeliveryFilter = (listParams) => {
  const key = 'sign_out_state';
  const options = DELIVERY_STATUS_TYPES;
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Delivery status',
    isClearable: true,
    options,
    value,
  };
};

export const communityFilter = (listParams, communities, setOnChange) => {
  const key = 'community';
  const options = [
    ...formatSelect(
      Object.values(communities).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];
  const value = getSelectValue(listParams?.[key], options);
  let filter = {
    key,
    defaultValue: null,
    placeholder: 'Select community',
    isClearable: true,
    options,
    value,
  };
  if (setOnChange) {
    filter = { ...filter, setOnChange };
  }
  return filter;
};

export const communityStatusFilter = (listParams) => {
  const key = 'approval_status';
  const options = [...COMMUNITY_STATUS_OPTIONS];
  const value = getSelectValue(listParams?.[key], options);

  return {
    key,
    defaultValue: value,
    placeholder: 'Select status',
    isClearable: true,
    options,
    value,
  };
};

export const srIdsFilter = (listParams, ids) => {
  const key = 'carson_service_requests_ids';
  const options = [...formatTextSelect(ids)];

  // an array of SR ids is represented as a comma separated list of ids
  // for example carson_service_requests_ids=1,2,3
  // to map each id value the string is splitted by the comma separator
  const selectedIds = listParams?.[key]?.split(',')?.filter(Boolean);

  // multi select requires an array of values
  // with the [{ label, value }] structure
  const value = selectedIds?.map((value) => getSelectValue(value, options));

  return {
    key,
    defaultValue: value,
    placeholder: 'Service Request ID',
    isClearable: true,
    options,
    value,
    multi: true,
    sortAlphabetically: false,
  };
};

export const eventsMultiCategoryFilter = (listParams) => {
  const key = 'category';
  const options = [...CATEGORIES_LIST];
  const selectedStatuses = listParams?.[key]
    ?.toString()
    ?.split(',')
    ?.filter(Boolean);

  const value = selectedStatuses?.map((value) =>
    getSelectValue(value, options),
  );

  return {
    key,
    defaultValue: value,
    placeholder: 'Category',
    isClearable: true,
    options,
    value,
    multi: true,
    isMultiWithCheckbox: true,
    width: '30rem',
  };
};

export const eventStatusFilter = (listParams) => {
  const key = 'upcoming';
  const options = EVENTS_STATUS_TYPES;
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: 'Event status',
    isClearable: true,
    options,
    value,
  };
};
